import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import {
  Box,
  Container,
  Stack,
  Typography,
  Button,
  TextField,
  InputAdornment,
  MenuItem,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';

import Loader from 'src/common/Loader';
import GroupList from 'src/components/groups/GroupList';
import NewGroup from 'src/components/groups/NewGroup';

import { getGroupList, setGroup } from 'src/redux/groupSlice';
import { GROUP_TYPES } from 'src/utils/constants';

const Groups = () => {
  const dispatch = useDispatch();

  const { groupList, groupType, loading } = useSelector(
    (state) => state.groups
  );

  const [openModal, setOpenModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    dispatch(getGroupList({ groupType, params: { name: searchText } }));
  }, [dispatch, groupType, searchText]);

  const handleEditGroup = (group) => {
    setSelectedGroup(group);
    setOpenModal(true);
  };

  return (
    <>
      <Helmet>
        <title>Groups | Retrack FMS</title>
      </Helmet>
      <Box
        sx={{
          backgroundColor: '#FFFFFF',
          minHeight: 'calc(100vh - 64px)',
          height: { xs: 'auto', md: 'calc(100vh - 64px)' },
          overflow: { xs: 'auto', md: 'hidden' },
          py: 2
        }}
      >
        <Container
          maxWidth="xl"
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0
          }}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            alignItems={{ xs: 'flex-start', md: 'center' }}
            justifyContent="space-between"
            spacing={3}
            sx={{ py: 2, mb: 2, flexShrink: 0 }}
          >
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 800,
                  color: '#111827',
                  letterSpacing: '-0.02em',
                  mb: 0.5
                }}
              >
                GROUPS
              </Typography>
              <Typography variant="body2" color="text.secondary" fontWeight={500}>
                Manage and organize system groups
              </Typography>
            </Box>

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              useFlexGap
              flexWrap={{ xs: 'nowrap', sm: 'wrap' }}
              sx={{ width: { xs: '100%', md: 'auto' } }}
            >
              <TextField
                size="small"
                placeholder="Search groups..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                sx={{
                  width: { xs: '100%', md: 260 },
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: '#F9FAFB'
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: 'text.disabled' }} />
                    </InputAdornment>
                  )
                }}
              />

              <TextField
                select
                size="small"
                value={groupType}
                onChange={(e) => dispatch(setGroup(e.target.value))}
                sx={{
                  minWidth: 200,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: '#F9FAFB'
                  }
                }}
              >
                {GROUP_TYPES.map((type) => (
                  <MenuItem key={type.id} value={type.id}>
                    {type.name}
                  </MenuItem>
                ))}
              </TextField>

              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  setSelectedGroup(null);
                  setOpenModal(true);
                }}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3
                }}
              >
                Add Group
              </Button>
            </Stack>
          </Stack>

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: 'hidden'
            }}
          >
            {loading ? (
              <Loader />
            ) : (
              <GroupList
                groups={groupList}
                onEditGroup={handleEditGroup}
              />
            )}
          </Box>
        </Container>

        <NewGroup
          open={openModal}
          initialValues={selectedGroup}
          handleClose={() => {
            setOpenModal(false);
            setSelectedGroup(null);
          }}
        />
      </Box>
    </>
  );
};

export default Groups;
